import { firebaseAuthService } from './firebaseAuthService';
import { apiService } from './apiService';
import { apiConfig } from '../config/api';
import {
  AuthResult,
  PlatformAuthResponse,
  PasswordResetResult,
  FirebaseUserData,
  AppleSignInFlow
} from '../types/auth';

/**
 * The URL fragment as it was when the page loaded, captured before React mounts and before any
 * Firebase call can run.
 *
 * The server-side Apple flow returns its custom token in the fragment, and the fragment is mutable
 * global state that several things rewrite: our own history.replaceState below, and the Firebase
 * SDK's redirect resolver, which tidies the URL when getRedirectResult() runs. Reading it after an
 * await is therefore a race, and it is a race we lost - the token was gone by the time the Apple
 * completion effect looked, so sign-in silently stopped at the login page with no error.
 *
 * Snapshotting at module load removes the race entirely rather than relying on effect ordering.
 */
const initialLocationHash =
  (typeof window !== 'undefined' ? window.location?.hash : '') ?? '';

/**
 * Whether this page load carries a server-side Apple sign-in result
 */
export const hasAppleCompletionFragment = (): boolean => {
  const raw = initialLocationHash.startsWith('#') ? initialLocationHash.slice(1) : initialLocationHash;
  if (!raw) {
    return false;
  }

  const params = new URLSearchParams(raw);
  return params.has('apple_token') || params.has('apple_error');
};

/**
 * Integrated Authentication Service
 * Handles the complete authentication flow from Firebase to Platform JWT
 */
export class IntegratedAuthService {
  /**
   * Complete sign-in flow: Firebase authentication + platform token exchange
   */
  async signIn(
    email: string,
    password: string,
    clientId: string,
    returnUrl?: string
  ): Promise<AuthResult & { platformResponse?: PlatformAuthResponse }> {
    try {
      console.log('Starting email authentication flow', { email, returnUrl, clientId });
      
      // Step 1: Validate return URL if provided
      if (returnUrl) {
        console.log('Validating return URL for email authentication');
        const validation = await this.validateReturnUrl(returnUrl, clientId);
        if (!validation.isValid) {
          console.error('Invalid return URL for email authentication:', validation.error);
          return {
            success: false,
            error: validation.error || 'errors.integrated.invalidReturnUrl'
          };
        }
      }

      // Step 2: Authenticate with Firebase
      const firebaseResult = await firebaseAuthService.signIn(email, password);
      
      if (!firebaseResult.success) {
        console.error('Firebase email authentication failed:', firebaseResult.error);
        return {
          success: false,
          error: firebaseResult.error
        };
      }

      console.log('Email authentication successful, exchanging token with backend');

      // Step 3: Get Firebase ID token
      const firebaseToken = await firebaseAuthService.getCurrentUserToken();
      
      if (!firebaseToken) {
        console.error('Failed to get Firebase ID token after email authentication');
        return {
          success: false,
          error: 'errors.integrated.firebaseTokenFailed'
        };
      }

      // Step 4: Exchange Firebase token for platform JWT
      try {
        console.log('Exchanging Firebase token for platform JWT (email auth)');
        const platformResponse = await apiService.authenticateWithFirebaseToken(
          firebaseToken,
          clientId,
          'email',
          returnUrl
        );

        console.log('Platform token exchange successful (email auth)', {
          hasToken: !!platformResponse.platformToken,
          redirectUrl: platformResponse.redirectUrl,
          expiresAt: platformResponse.expiresAt
        });

        return {
          success: true,
          user: firebaseResult.user,
          platformResponse
        };
      } catch (apiError) {
        console.error('Platform authentication failed for email user:', apiError);
        
        // If platform authentication fails, sign out from Firebase to maintain consistency
        await firebaseAuthService.signOut();
        
        return {
          success: false,
          error: apiError instanceof Error ? apiError.message : 'errors.integrated.platformAuthFailed'
        };
      }
    } catch (error) {
      console.error('Email authentication flow failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'errors.integrated.authFailed'
      };
    }
  }

  /**
   * Complete Google sign-in flow: Firebase Google authentication + platform token exchange
   */
  async signInWithGoogle(
    clientId: string,
    returnUrl?: string
  ): Promise<AuthResult & { platformResponse?: PlatformAuthResponse }> {
    try {
      console.log('Starting Google authentication flow', { returnUrl, clientId });
      
      // Step 1: Validate return URL if provided
      if (returnUrl) {
        console.log('Validating return URL for Google authentication');
        const validation = await this.validateReturnUrl(returnUrl, clientId);
        if (!validation.isValid) {
          console.error('Invalid return URL for Google authentication:', validation.error);
          return {
            success: false,
            error: validation.error || 'errors.integrated.invalidReturnUrl'
          };
        }
      }
      
      // Step 2: Authenticate with Google via Firebase
      const firebaseResult = await firebaseAuthService.signInWithGoogle();
      
      if (!firebaseResult.success) {
        console.error('Firebase Google authentication failed:', firebaseResult.error);
        return {
          success: false,
          error: firebaseResult.error
        };
      }

      // If redirect flow was initiated, return success without platform response
      if (!firebaseResult.user) {
        console.log('Google redirect flow initiated, waiting for redirect result');
        return {
          success: true
        };
      }

      console.log('Google authentication successful, exchanging token with backend', {
        uid: firebaseResult.user.uid,
        email: firebaseResult.user.email,
        providerId: firebaseResult.user.providerId
      });

      // Step 2: Get Firebase ID token (required for backend validation)
      const firebaseIdToken = await firebaseAuthService.getCurrentUserToken();
      
      if (!firebaseIdToken) {
        console.error('Failed to get Firebase ID token after Google authentication');
        return {
          success: false,
          error: 'errors.integrated.firebaseTokenFailed'
        };
      }

      // Step 3: Get Google ID token and access token from credential if available
      const googleIdToken = (firebaseResult as any).idToken;
      const googleAccessToken = (firebaseResult as any).accessToken;
      console.log('Tokens available after Google sign-in', {
        hasFirebaseIdToken: !!firebaseIdToken,
        hasGoogleIdToken: !!googleIdToken,
        hasGoogleAccessToken: !!googleAccessToken
      });

      // Step 4: Exchange Firebase ID token for platform JWT
      // Note: The backend validates the Firebase ID token which contains Google authentication info
      // We send the Firebase ID token as the primary token, and include Google ID token in metadata if available
      try {
        console.log('Exchanging Firebase ID token for platform JWT', {
          hasGoogleIdToken: !!googleIdToken,
          hasGoogleAccessToken: !!googleAccessToken
        });
        
        // Prepare metadata with Google ID token if available
        const metadata: Record<string, string> = {};
        if (googleIdToken) {
          metadata.googleIdToken = googleIdToken;
        }
        if (googleAccessToken) {
          metadata.googleAccessToken = googleAccessToken;
        }
        
        // For Google authentication, we send:
        // 1. Firebase ID token (primary - used for validation)
        // 2. Google ID token (in metadata - for additional validation if needed)
        // 3. Google access token (in metadata - for Google API calls if needed)
        const platformResponse = await apiService.authenticateWithFirebaseToken(
          firebaseIdToken,
          clientId,
          'google',
          returnUrl,
          Object.keys(metadata).length > 0 ? metadata : undefined
        );

        console.log('Platform token exchange successful', {
          hasToken: !!platformResponse.platformToken,
          redirectUrl: platformResponse.redirectUrl,
          expiresAt: platformResponse.expiresAt
        });

        return {
          success: true,
          user: firebaseResult.user,
          platformResponse
        };
      } catch (apiError) {
        console.error('Platform authentication failed for Google user:', apiError);
        
        // If platform authentication fails, sign out from Firebase to maintain consistency
        await firebaseAuthService.signOut();
        
        return {
          success: false,
          error: apiError instanceof Error ? apiError.message : 'errors.integrated.platformAuthFailed'
        };
      }
    } catch (error) {
      console.error('Google authentication flow failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'errors.integrated.googleAuthFailed'
      };
    }
  }

  /**
   * Handle Google redirect result and exchange for platform JWT
   */
  async handleGoogleRedirectResult(
    clientId: string,
    returnUrl?: string
  ): Promise<(AuthResult & { platformResponse?: PlatformAuthResponse }) | null> {
    try {
      console.log('Checking for Google redirect result', { clientId, returnUrl });
      
      // Step 1: Validate return URL if provided
      if (returnUrl) {
        console.log('Validating return URL for Google redirect result');
        const validation = await this.validateReturnUrl(returnUrl, clientId);
        if (!validation.isValid) {
          console.error('Invalid return URL for Google redirect result:', validation.error);
          return {
            success: false,
            error: validation.error || 'errors.integrated.invalidReturnUrl'
          };
        }
      }
      
      // Step 2: Check for Google redirect result
      const firebaseResult = await firebaseAuthService.getGoogleRedirectResult();
      
      // No redirect result found (normal case)
      if (!firebaseResult.success && !firebaseResult.error) {
        console.log('No Google redirect result found');
        return null;
      }

      if (!firebaseResult.success) {
        console.error('Google redirect result failed:', firebaseResult.error);
        return {
          success: false,
          error: firebaseResult.error
        };
      }

      if (!firebaseResult.user) {
        console.error('No user data received from Google redirect result');
        return {
          success: false,
          error: 'errors.integrated.noUserData'
        };
      }

      console.log('OAuth redirect result successful, exchanging token with backend', {
        uid: firebaseResult.user.uid,
        email: firebaseResult.user.email,
        providerId: firebaseResult.user.providerId
      });

      // The redirect result may come from either the Google or the Apple flow
      const redirectProviderId = firebaseResult.user.providerId === 'apple.com' ? 'apple' : 'google';

      // Step 2: Get Firebase ID token (required for backend validation)
      const firebaseIdToken = await firebaseAuthService.getCurrentUserToken();
      
      if (!firebaseIdToken) {
        console.error('Failed to get Firebase ID token from redirect result');
        return {
          success: false,
          error: 'errors.integrated.firebaseTokenFailed'
        };
      }

      // Step 3: Get Google ID token and access token from credential if available
      const googleIdToken = (firebaseResult as any).idToken;
      const googleAccessToken = (firebaseResult as any).accessToken;
      console.log('Tokens available after Google redirect', {
        hasFirebaseIdToken: !!firebaseIdToken,
        hasGoogleIdToken: !!googleIdToken,
        hasGoogleAccessToken: !!googleAccessToken
      });

      // Step 4: Exchange Firebase ID token for platform JWT
      // Note: The backend validates the Firebase ID token which contains Google authentication info
      // We send the Firebase ID token as the primary token, and include Google ID token in metadata if available
      try {
        console.log('Exchanging Firebase ID token for platform JWT (redirect flow)', {
          hasGoogleIdToken: !!googleIdToken,
          hasGoogleAccessToken: !!googleAccessToken
        });
        
        // Prepare metadata with Google ID token if available
        const metadata: Record<string, string> = {};
        if (googleIdToken) {
          metadata.googleIdToken = googleIdToken;
        }
        if (googleAccessToken) {
          metadata.googleAccessToken = googleAccessToken;
        }
        
        // For Google authentication, we send:
        // 1. Firebase ID token (primary - used for validation)
        // 2. Google ID token (in metadata - for additional validation if needed)
        // 3. Google access token (in metadata - for Google API calls if needed)
        const platformResponse = await apiService.authenticateWithFirebaseToken(
          firebaseIdToken,
          clientId,
          redirectProviderId,
          returnUrl,
          Object.keys(metadata).length > 0 ? metadata : undefined
        );

        console.log('Platform token exchange successful (redirect flow)', {
          hasToken: !!platformResponse.platformToken,
          redirectUrl: platformResponse.redirectUrl,
          expiresAt: platformResponse.expiresAt
        });

        return {
          success: true,
          user: firebaseResult.user,
          platformResponse
        };
      } catch (apiError) {
        console.error('Platform authentication failed for Google redirect user:', apiError);
        
        // If platform authentication fails, sign out from Firebase to maintain consistency
        await firebaseAuthService.signOut();
        
        return {
          success: false,
          error: apiError instanceof Error ? apiError.message : 'errors.integrated.platformAuthFailed'
        };
      }
    } catch (error) {
      console.error('Failed to process Google redirect result:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'errors.integrated.googleRedirectFailed'
      };
    }
  }

  /**
   * Complete sign-out flow: Platform token revocation + Firebase sign-out
   */
  async signOut(clientId: string): Promise<AuthResult> {
    try {
      // Step 1: Get current Firebase token for backend revocation
      const firebaseToken = await firebaseAuthService.getCurrentUserToken();
      
      // Step 2: Revoke token on backend (if we have a token)
      if (firebaseToken) {
        try {
          await apiService.signOut(firebaseToken, clientId);
        } catch (apiError) {
          // Log the error but continue with Firebase sign-out
          console.warn('Backend sign-out failed:', apiError);
        }
      }

      // Step 3: Sign out from Firebase
      const firebaseResult = await firebaseAuthService.signOut();
      
      return firebaseResult;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'errors.integrated.signOutFailed'
      };
    }
  }

  /**
   * Password reset flow (Firebase only)
   */
  async resetPassword(email: string): Promise<PasswordResetResult> {
    return firebaseAuthService.resetPassword(email);
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): FirebaseUserData | null {
    return firebaseAuthService.getCurrentUser();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return firebaseAuthService.isAuthenticated();
  }

  /**
   * Get current Firebase token
   */
  async getCurrentUserToken(): Promise<string | null> {
    return firebaseAuthService.getCurrentUserToken();
  }

  /**
   * Validate return URL with backend
   */
  async validateReturnUrl(returnUrl: string, clientId: string): Promise<{ isValid: boolean; error?: string }> {
    try {
      return await apiService.validateReturnUrl(returnUrl, clientId);
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'errors.integrated.returnUrlValidationFailed'
      };
    }
  }

  /**
   * Exchange current Firebase token for platform JWT (for already authenticated users)
   */
  async exchangeTokenForPlatformJWT(clientId: string, returnUrl?: string): Promise<PlatformAuthResponse | null> {
    try {
      console.log('Exchanging current Firebase token for platform JWT', { returnUrl, clientId });
      
      const firebaseToken = await this.getCurrentUserToken();
      
      if (!firebaseToken) {
        console.error('No Firebase token available for exchange');
        throw new Error('errors.integrated.noFirebaseToken');
      }

      const currentUser = this.getCurrentUser();
      console.log('Current user for token exchange', {
        uid: currentUser?.uid,
        email: currentUser?.email,
        providerId: currentUser?.providerId
      });

      // Determine provider ID from current user's providerId
      let providerId: 'google' | 'apple' | 'email' = 'email';
      if (currentUser?.providerId === 'google.com') {
        providerId = 'google';
      } else if (currentUser?.providerId === 'apple.com') {
        providerId = 'apple';
      }

      const platformResponse = await apiService.authenticateWithFirebaseToken(firebaseToken, clientId, providerId, returnUrl);
      
      console.log('Token exchange successful', {
        hasToken: !!platformResponse.platformToken,
        redirectUrl: platformResponse.redirectUrl,
        expiresAt: platformResponse.expiresAt
      });

      return platformResponse;
    } catch (error) {
      console.error('Token exchange failed:', error);
      return null;
    }
  }

  /**
   * Complete Apple sign-in flow: Firebase Apple authentication + platform token exchange.
   * Mirrors the Google flow - the Firebase SDK performs Sign in with Apple and the
   * backend receives a Firebase ID token.
   */
  /**
   * Hands the browser to the identity server's Apple authorize endpoint, which redirects on to
   * Apple with this client's Services ID
   */
  private redirectToAppleWebFlow(clientId: string, returnUrl?: string, flowKey?: string): void {
    const params = new URLSearchParams({ client_id: clientId });

    // Names which Apple Developer team to sign in through. Omitted only for a client with a single
    // server-side flow, where the identity server can resolve it unambiguously; with several
    // configured it refuses to guess rather than risk returning someone else's identity.
    if (flowKey) {
      params.set('flow', flowKey);
    }

    if (returnUrl) {
      params.set('returnUrl', returnUrl);
    }

    // Carried through the Apple round trip so the completion page comes back in the same language
    const lang = new URLSearchParams(window.location.search).get('lang');
    if (lang) {
      params.set('lang', lang);
    }

    window.location.assign(`${apiConfig.baseUrl}/api/auth/apple/start?${params.toString()}`);
  }

  /**
   * Finishes the server-side Apple flow if the current URL carries its result.
   * The identity server returns the Firebase custom token in the fragment, so it never reaches
   * server logs, proxies or Referer headers.
   *
   * @returns null when this page load is not an Apple completion
   */
  async completeAppleWebFlow(
    clientId: string
  ): Promise<(AuthResult & { platformResponse?: PlatformAuthResponse }) | null> {
    // Read the snapshot taken at module load, not window.location.hash - see initialLocationHash
    const rawFragment = initialLocationHash.startsWith('#')
      ? initialLocationHash.slice(1)
      : initialLocationHash;

    if (!rawFragment) {
      return null;
    }

    const params = new URLSearchParams(rawFragment);
    const customToken = params.get('apple_token');
    const appleError = params.get('apple_error');

    if (!customToken && !appleError) {
      return null;
    }

    // return_url comes back in the query string, not the fragment - the app reads it from
    // window.location.search on mount and will not render without it
    const returnUrl = new URLSearchParams(window.location.search).get('return_url') ?? undefined;

    // Drop only the fragment, so a refresh cannot replay the custom token while client_id,
    // return_url and lang stay in the URL for the rest of the app
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

    if (appleError) {
      console.log('Apple sign-in returned an error', { appleError });

      // Cancellation is a normal outcome and should not surface as a failure
      return appleError === 'user_cancelled_authorize'
        ? { success: false, error: undefined }
        : { success: false, error: 'Apple sign-in could not be completed. Please try again.' };
    }

    console.log('Completing Apple sign-in with custom token', { clientId, returnUrl });

    const firebaseResult = await firebaseAuthService.signInWithCustomToken(customToken!);
    if (!firebaseResult.success) {
      return {
        success: false,
        error: firebaseResult.error
      };
    }

    const platformResponse = await this.exchangeTokenForPlatformJWT(clientId, returnUrl);

    if (!platformResponse) {
      // Firebase sign-in succeeded but the platform exchange did not. Returning success with no
      // platformResponse leaves the page signed in and idle: AuthenticationPage only redirects when
      // a platform response exists, so the user sees neither a redirect nor an error. Report the
      // failure instead - exchangeTokenForPlatformJWT has already logged the underlying cause.
      console.error('Apple sign-in completed but the platform token exchange returned nothing');

      return {
        success: false,
        error: 'Signed in with Apple, but completing the session failed. Please try again.'
      };
    }

    return {
      ...firebaseResult,
      platformResponse
    };
  }

  async signInWithApple(
    clientId: string,
    returnUrl?: string,
    flow: AppleSignInFlow = 'firebase',
    flowKey?: string
  ): Promise<AuthResult & { platformResponse?: PlatformAuthResponse }> {
    try {
      console.log('Starting Apple authentication flow', { returnUrl, clientId });

      // Step 1: Validate return URL if provided
      if (returnUrl) {
        console.log('Validating return URL for Apple authentication');
        const validation = await this.validateReturnUrl(returnUrl, clientId);
        if (!validation.isValid) {
          console.error('Invalid return URL for Apple authentication:', validation.error);
          return {
            success: false,
            error: validation.error || 'errors.integrated.invalidReturnUrl'
          };
        }
      }

      // Step 2: The Firebase popup can only ever authenticate users from the Apple Developer team
      // configured on the Firebase project. Users from the other team must go through the identity
      // server's own flow, or Apple returns a different user identifier and a different private
      // relay address for the same person. The caller picks, because only the user knows which app
      // they signed up in.
      if (flow === 'server') {
        console.log('Using the server-side Apple flow, redirecting to the identity server', { flowKey });
        this.redirectToAppleWebFlow(clientId, returnUrl, flowKey);

        // The browser is navigating away; nothing further resolves here
        return { success: true };
      }

      // Step 3: Authenticate with Apple via Firebase
      const firebaseResult = await firebaseAuthService.signInWithApple();

      if (!firebaseResult.success) {
        console.error('Firebase Apple authentication failed:', firebaseResult.error);
        return {
          success: false,
          error: firebaseResult.error
        };
      }

      // If redirect flow was initiated, return success without platform response
      if (!firebaseResult.user) {
        console.log('Apple redirect flow initiated, waiting for redirect result');
        return {
          success: true
        };
      }

      console.log('Apple authentication successful, exchanging token with backend', {
        uid: firebaseResult.user.uid,
        email: firebaseResult.user.email,
        providerId: firebaseResult.user.providerId
      });

      // Step 3: Get Firebase ID token (required for backend validation)
      const firebaseIdToken = await firebaseAuthService.getCurrentUserToken();

      if (!firebaseIdToken) {
        console.error('Failed to get Firebase ID token after Apple authentication');
        return {
          success: false,
          error: 'errors.integrated.firebaseTokenFailed'
        };
      }

      // Step 4: Exchange Firebase ID token for platform JWT
      try {
        console.log('Exchanging Firebase ID token for platform JWT (Apple auth)');
        const platformResponse = await apiService.authenticateWithFirebaseToken(
          firebaseIdToken,
          clientId,
          'apple',
          returnUrl
        );

        console.log('Platform token exchange successful (Apple auth)', {
          hasToken: !!platformResponse.platformToken,
          redirectUrl: platformResponse.redirectUrl,
          expiresAt: platformResponse.expiresAt
        });

        return {
          success: true,
          user: firebaseResult.user,
          platformResponse
        };
      } catch (apiError) {
        console.error('Platform authentication failed for Apple user:', apiError);

        // If platform authentication fails, sign out from Firebase to maintain consistency
        await firebaseAuthService.signOut();

        return {
          success: false,
          error: apiError instanceof Error ? apiError.message : 'errors.integrated.platformAuthFailed'
        };
      }
    } catch (error) {
      console.error('Apple authentication flow failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'errors.integrated.appleAuthFailed'
      };
    }
  }

  /**
   * Check if Apple Sign-In is available.
   * The Firebase SDK popup/redirect flow works in any browser environment with popup support.
   */
  isAppleSignInAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.open === 'function';
  }

  /**
   * Check if user has valid authentication session
   */
  async hasValidSession(): Promise<boolean> {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        return false;
      }

      const firebaseToken = await this.getCurrentUserToken();
      if (!firebaseToken) {
        return false;
      }

      // Token exists and user is authenticated
      return true;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const integratedAuthService = new IntegratedAuthService();